/**
 * 附件提取/识别的状态标签。
 *
 * 【穷尽渲染，未知值一律灰显】
 * `extractStatus` / `handwritingStatus` 都是**穷尽枚举**，前端遇到不认识的值
 * 必须渲染成灰色的「未知状态」。绝不能兜底成绿色——"未知值静默落到看起来正常的
 * 分支"正是本项目已经付出过代价的那类 bug（沙箱伪造的数据长期显示为「校验成功」）。
 *
 * 【为什么合同与手写共用一份实现】
 * 变的是枚举值与配色，不变的是上面那条规则。两处各写一遍"未知值怎么办"，
 * 迟早有一处会漏掉——而漏掉的那次恰好就是伪造数据被显示成绿色的时候。
 * 因此把渲染与文字拼装抽成 `renderStatusTag`，两套枚举各自提供映射表。
 */

import { Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  DisconnectOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FileUnknownOutlined,
  LockOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  ContractExtractStatus,
  CONTRACT_EXTRACT_STATUS_LABELS,
  HANDWRITING_STATUS_LABELS,
  HandwritingStatus,
  ProviderMode,
} from '@ilc/shared';

/** 通道来源。与校验报告的 ProviderModeTag 同一套口径 */
const MODE_LABELS: Record<string, string> = {
  [ProviderMode.LOCAL]: '本地解析',
  [ProviderMode.REAL]: '阿里云识别',
  [ProviderMode.UNAVAILABLE]: '未使用通道',
  [ProviderMode.SANDBOX]: '沙箱',
  [ProviderMode.MANUAL]: '人工',
};

interface StatusPresentation {
  labels: Record<string, string>;
  colors: Record<string, string>;
  icons: Record<string, React.ReactNode>;
}

export interface AttachmentStatusTagProps {
  status: string | null | undefined;
  /** 具体实现名，如 pdf-text / aliyun-RecognizeHandwriting */
  impl?: string | null;
  mode?: string | null;
  /** 失败原因，有值时放进 Tooltip */
  message?: string | null;
  /** 缺失的配置项，展示给运维照单补齐 */
  missing?: string[] | null;
}

function renderStatusTag(
  { labels, colors, icons }: StatusPresentation,
  { status, impl, mode, message, missing }: AttachmentStatusTagProps,
): React.ReactElement {
  const known = status != null && status in labels;
  const label = known ? labels[status]! : '未知状态';
  const color = known ? colors[status] : 'default';
  const icon = known ? icons[status] : <FileUnknownOutlined />;

  const modeText = mode ? (MODE_LABELS[mode] ?? mode) : null;
  const detail = [message, missing?.length ? `需补齐：${missing.join('、')}` : null]
    .filter(Boolean)
    .join('\n');

  const tag = (
    <Tag color={color} icon={icon} style={{ marginInlineEnd: 0 }}>
      {label}
      {impl ? ` · ${impl}` : ''}
      {modeText ? `（${modeText}）` : ''}
    </Tag>
  );

  return detail ? (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{detail}</span>}>{tag}</Tooltip>
  ) : (
    tag
  );
}

/* ==========================================================================
 * 合同附件解析
 * ========================================================================== */

const CONTRACT_COLORS: Record<ContractExtractStatus, string> = {
  [ContractExtractStatus.OK]: 'green',
  [ContractExtractStatus.PARTIAL]: 'orange',
  [ContractExtractStatus.NO_TEXT_LAYER]: 'orange',
  // 未激活是红色：它意味着"这次没有做任何真实识别"，与"识别了但内容少"不同
  [ContractExtractStatus.UNAVAILABLE]: 'red',
  [ContractExtractStatus.ENCRYPTED]: 'orange',
  [ContractExtractStatus.FAILED]: 'red',
  [ContractExtractStatus.TIMEOUT]: 'red',
};

const CONTRACT_ICONS: Record<ContractExtractStatus, React.ReactNode> = {
  [ContractExtractStatus.OK]: <CheckCircleOutlined />,
  [ContractExtractStatus.PARTIAL]: <WarningOutlined />,
  [ContractExtractStatus.NO_TEXT_LAYER]: <FileSearchOutlined />,
  [ContractExtractStatus.UNAVAILABLE]: <DisconnectOutlined />,
  [ContractExtractStatus.ENCRYPTED]: <LockOutlined />,
  [ContractExtractStatus.FAILED]: <ExclamationCircleOutlined />,
  [ContractExtractStatus.TIMEOUT]: <ExclamationCircleOutlined />,
};

const CONTRACT_PRESENTATION: StatusPresentation = {
  labels: CONTRACT_EXTRACT_STATUS_LABELS,
  colors: CONTRACT_COLORS,
  icons: CONTRACT_ICONS,
};

export function AttachmentStatusTag(props: AttachmentStatusTagProps): React.ReactElement {
  return renderStatusTag(CONTRACT_PRESENTATION, props);
}

/* ==========================================================================
 * 发票附件的手写内容
 * ========================================================================== */

const HANDWRITING_COLORS: Record<HandwritingStatus, string> = {
  [HandwritingStatus.OK]: 'green',
  [HandwritingStatus.PARTIAL]: 'orange',
  /**
   * 「通道未激活」是红色——它意味着**这次没有做任何真实识别**，
   * 与"识别了但内容少"完全不同，不能让用户以为已经核对过手写内容。
   */
  [HandwritingStatus.UNAVAILABLE]: 'red',
  /**
   * 「该格式不提取」是灰色而非红色：PDF 只存档是**设计如此**，不是故障。
   * 用红色会让人以为系统坏了，反复重试，而正确动作是补传一张照片。
   */
  [HandwritingStatus.UNSUPPORTED_FORMAT]: 'default',
  [HandwritingStatus.FAILED]: 'red',
  [HandwritingStatus.TIMEOUT]: 'red',
};

const HANDWRITING_ICONS: Record<HandwritingStatus, React.ReactNode> = {
  [HandwritingStatus.OK]: <CheckCircleOutlined />,
  [HandwritingStatus.PARTIAL]: <WarningOutlined />,
  [HandwritingStatus.UNAVAILABLE]: <DisconnectOutlined />,
  [HandwritingStatus.UNSUPPORTED_FORMAT]: <FileUnknownOutlined />,
  [HandwritingStatus.FAILED]: <ExclamationCircleOutlined />,
  [HandwritingStatus.TIMEOUT]: <ExclamationCircleOutlined />,
};

const HANDWRITING_PRESENTATION: StatusPresentation = {
  labels: HANDWRITING_STATUS_LABELS,
  colors: HANDWRITING_COLORS,
  icons: HANDWRITING_ICONS,
};

export function HandwritingStatusTag(props: AttachmentStatusTagProps): React.ReactElement {
  return renderStatusTag(HANDWRITING_PRESENTATION, props);
}

export default AttachmentStatusTag;
